import { OrderedMap } from 'immutable';
import FileEntity from 'entities/File';
import { Backbone$Id } from 'types/typescript/Backbone';
// @ts-ignore
import { fetchFileByIdRequest, uploadFileRequest } from 'clients/api/fileUploads';

/*
 * Files are stored in a nested data structure. Each UploadRequestGroup is stored
 * by a fileContextKey (such as thread-uuid). Within each UploadRequestGroup, UploadRequestData
 * objects are stored by fileKey (such as filename or fileId for quick promos)
 */
export type UploadRequestData = {
  file?: FileEntity | null;
  blob: File;
  progressPercentage: number;
  thumbnail?: string;
  isError?: boolean;
  error?: Object;
};

export type UploadRequestGroup = OrderedMap<Backbone$Id, UploadRequestData>;
export type State = OrderedMap<string, UploadRequestGroup>;

export const MODAL_FILE_CONTEXT_KEY = 'MODAL_FILE_CONTEXT_KEY'; // we only support one file group across all modals

export const initialState: State = new (OrderedMap as any)();

export default function reducer(state: State = initialState, action: any): State {
  const fileGroup = state.get(action.fileContextKey) || OrderedMap();

  // hack - this could potentially still return undefined...
  const uploadRequestData = fileGroup.get(action.fileKey) as UploadRequestData;

  switch (action.type) {
    case 'fileUploads/UPLOAD_REQUEST': {
      const updatedGroup = fileGroup.set(action.fileKey, {
        ...uploadRequestData,
        progressPercentage: 0,
        blob: action.blob,
      });
      return state.set(action.fileContextKey, updatedGroup);
    }

    case 'fileUploads/UPLOAD_REQUEST_SUCCESS': {
      const updatedGroup = fileGroup.set(action.fileKey, {
        ...uploadRequestData,
        file: action.file,
        progressPercentage: 100,
      });
      return state.set(action.fileContextKey, updatedGroup);
    }

    case 'fileUploads/UPLOAD_REQUEST_FAIL': {
      const updatedGroup = fileGroup.set(action.fileKey, {
        ...uploadRequestData,
        isError: true,
        error: action.error,
      });
      return state.set(action.fileContextKey, updatedGroup);
    }

    case 'fileUploads/REMOVE_FILE': {
      return state.set(action.fileContextKey, fileGroup.remove(action.fileKey));
    }

    case 'fileUploads/RESET_FILE': {
      const updatedGroup = fileGroup.set(action.fileKey, {
        ...uploadRequestData,
        isError: false,
        progressPercentage: 0,
        file: null,
      });
      return state.set(action.fileContextKey, updatedGroup);
    }

    case 'fileUploads/LOAD_THUMBNAIL': {
      const updatedGroup = fileGroup.set(action.fileKey, {
        ...uploadRequestData,
        thumbnail: action.thumbnail,
      });
      return state.set(action.fileContextKey, updatedGroup);
    }

    case 'fileUploads/PROGRESS_UPDATE': {
      const updatedGroup = fileGroup.set(action.fileKey, {
        ...uploadRequestData,
        progressPercentage: action.progressPercentage,
      });
      return state.set(action.fileContextKey, updatedGroup);
    }

    default: {
      return state;
    }
  }
}

export type UploadRequestAction = {
  type: 'fileUploads/UPLOAD_REQUEST';
  fileContextKey: string;
  fileKey: Backbone$Id;
  blob: File;
};

export function uploadRequestAction(
  fileContextKey: string,
  fileKey: Backbone$Id,
  blob: File
): UploadRequestAction {
  return {
    type: 'fileUploads/UPLOAD_REQUEST',
    fileContextKey,
    fileKey,
    blob,
  };
}

export type RemoveFileAction = {
  type: 'fileUploads/REMOVE_FILE';
  fileContextKey: string;
  fileKey: Backbone$Id;
};

export function removeFile(fileContextKey: string, fileKey: Backbone$Id): RemoveFileAction {
  return {
    type: 'fileUploads/REMOVE_FILE',
    fileContextKey,
    fileKey,
  };
}

export type ResetFileAction = {
  type: 'fileUploads/RESET_FILE';
  fileContextKey: string;
  fileKey: Backbone$Id;
};

export function resetFile(fileContextKey: string, fileKey: Backbone$Id): ResetFileAction {
  return {
    type: 'fileUploads/RESET_FILE',
    fileContextKey,
    fileKey,
  };
}

export type LoadThumbnailAction = {
  type: 'fileUploads/LOAD_THUMBNAIL';
  fileContextKey: string;
  fileKey: Backbone$Id;
  thumbnail: string;
};

export function loadThumbnail(
  fileContextKey: string,
  fileKey: Backbone$Id,
  thumbnail: string
): LoadThumbnailAction {
  return {
    type: 'fileUploads/LOAD_THUMBNAIL',
    fileContextKey,
    fileKey,
    thumbnail,
  };
}

export type ProgressUpdateAction = {
  type: 'fileUploads/PROGRESS_UPDATE';
  fileContextKey: string;
  fileKey: Backbone$Id;
  progressPercentage: number;
};

export function progressUpdate(
  fileContextKey: string,
  fileKey: Backbone$Id,
  progressPercentage: number
): ProgressUpdateAction {
  return {
    type: 'fileUploads/PROGRESS_UPDATE',
    fileContextKey,
    fileKey,
    progressPercentage,
  };
}

export type UploadRequestSuccessAction = {
  type: 'fileUploads/UPLOAD_REQUEST_SUCCESS';
  fileContextKey: string;
  fileKey: Backbone$Id;
  file: FileEntity;
};

export function uploadRequestSuccess(
  fileContextKey: string,
  fileKey: Backbone$Id,
  file: FileEntity
): UploadRequestSuccessAction {
  return {
    type: 'fileUploads/UPLOAD_REQUEST_SUCCESS',
    fileContextKey,
    fileKey,
    file,
  };
}

export type UploadRequestFailAction = {
  type: 'fileUploads/UPLOAD_REQUEST_FAIL';
  fileContextKey: string;
  fileKey: Backbone$Id;
  error?: Object;
};

export function uploadRequestFail(
  fileContextKey: string,
  fileKey: Backbone$Id,
  error?: Object
): UploadRequestFailAction {
  return {
    type: 'fileUploads/UPLOAD_REQUEST_FAIL',
    fileContextKey,
    fileKey,
    error,
  };
}

export function fetchFileById(fileContextKey: string, fileId: Backbone$Id) {
  return async (dispatch: Function) => {
    try {
      const file = await fetchFileByIdRequest(fileId);
      dispatch(uploadRequestSuccess(fileContextKey, fileId, file));
    } catch (error) {
      console.error('File retrieval failure', error);
    }
  };
}

export type UploadRequestSuccessHandler = (arg0: FileEntity) => {};
export type UploadRequestFailureHandler = (error: Object) => {};

export function uploadRequest(
  fileContextKey: string,
  fileKey: Backbone$Id,
  blob: File,
  onSuccess?: UploadRequestSuccessHandler,
  onFailure?: UploadRequestFailureHandler,
  privateUpload: boolean = false
) {
  return async (dispatch: Function) => {
    dispatch(uploadRequestAction(fileContextKey, fileKey, blob));

    try {
      const file = await uploadFileRequest(fileContextKey, fileKey, blob, privateUpload);
      if (onSuccess) {
        onSuccess(file);
      }
      dispatch(uploadRequestSuccess(fileContextKey, fileKey, file));
    } catch (error) {
      if (onFailure) {
        onFailure(error);
      }
      dispatch(uploadRequestFail(fileContextKey, fileKey, error));
    }
  };
}

export type FileUploadsAction =
  | UploadRequestAction
  | UploadRequestSuccessAction
  | UploadRequestFailAction
  | RemoveFileAction
  | ResetFileAction
  | LoadThumbnailAction
  | ProgressUpdateAction;
